import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";
import RotatingCoin from "@/components/RotatingCoin";
import EmailForm from "@/components/EmailForm";

const Hero = () => {
  const { t } = useI18n();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative isolate overflow-hidden grid-bg"
    >
      <div className="absolute inset-0 radial-glow pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[rgba(212,175,55,0.4)] to-transparent" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-24 sm:pt-28 sm:pb-32">
        <div className="flex flex-col items-center text-center">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="status-badge"
            className="pill-badge"
          >
            <span className="pulse-dot" />
            <span>{t.hero.badge}</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.1, ease: "easeOut" }}
            className="mt-10 sm:mt-12"
          >
            <RotatingCoin variant="hero" size={340} testId="hero-coin" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            data-testid="hero-title"
            className="font-heading mt-10 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight"
          >
            <span className="gold-gradient-text">{t.hero.title}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            data-testid="hero-subtitle"
            className="mt-3 text-xs sm:text-sm tracking-[0.32em] uppercase text-[var(--gold)]"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            data-testid="hero-tagline"
            className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-[var(--text-muted)]"
          >
            {t.hero.tagline}
          </motion.p>

          <div id="notify" className="mt-10 w-full">
            <EmailForm />
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-10 inline-flex items-center gap-3 text-xs text-[var(--text-muted)]"
            data-testid="hero-status-line"
          >
            <span className="h-px w-10 bg-[rgba(212,175,55,0.35)]" />
            <span className="tracking-[0.24em] uppercase">
              {t.status.development}
            </span>
            <span className="h-px w-10 bg-[rgba(212,175,55,0.35)]" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
